import { ContentPanel, HomeContentBlock, StreamGroup } from 'admin/interfaces'
import {
  AnyContent,
  Article,
  ARTICLE_TYPE,
  ContentItem,
  Feed,
  Gif,
  GIF_TYPE,
  InteractionTrackingSource,
  LiveArticleData,
  MyFile,
  Paginated,
  Photo,
  PHOTO_TYPE,
  STATUS_TYPE,
  Stream,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import { TEXT_TYPE } from 'interfaces/Content/ContentType'
import { STOCK_GIF_TYPE, STOCK_PHOTO_TYPE, STOCK_VIDEO_TYPE } from 'interfaces/Content/StockContentTypes'

export const CONTENT_STATE_VERSION = 'content:11'
export interface ContentCacheEntity {
  content: Readonly<Paginated<string[]>>
  timer: Readonly<number>
}

export interface FavoritesState {
  [type: string]: { [id: string]: boolean }
}

export type FavoritesStore = {
  saved: FavoritesState
  added: { [type: string]: { [id: string]: ContentItem } }
  removed: FavoritesState
  live: Record<string, boolean>
  isFetching: boolean
}

export type SelectableContent = (Photo | Article | Gif | Video | MyFile) & { order: number }
export interface ContentSelectionState {
  items: Record<string, SelectableContent>
  itemsOnPage: Record<string, SelectableContent>
  liveArticleData: Record<string, LiveArticleData & { id: string }>
  panelOpen: boolean
}

export interface ContentState {
  streams: Readonly<{ [key: string]: Readonly<Stream> }>
  feeds: Readonly<{ [key: string]: Readonly<Feed> }>
  user: Readonly<{
    streams: Array<Stream>
    feeds: Array<string>
  }>
  curatedStreams: Readonly<{
    popular: ReadonlyArray<Stream>
    industries: ReadonlyArray<Stream>
  }>
  recentSources: Readonly<{ [key: string]: ReadonlyArray<InteractionTrackingSource> }>
  selectedFeedsToSave: Readonly<Feed[]>
  cache: Readonly<{
    data: Readonly<{
      [id: string]: Readonly<{
        item: AnyContent
        count: number
      }>
    }>
    sources: Readonly<{ [key: string]: ContentCacheEntity }>
  }>
  favorites: FavoritesStore
  blocks: HomeContentBlock[]
  panels: ContentPanel[]
  groups: Record<string, StreamGroup>
  recentStatusesSearches: string[]
  recentQuotesSearches: Array<{ term: string, isTag: boolean }>
  selection: ContentSelectionState
}

export function initialState(): ContentState {
  return Object.freeze({
    streams: Object.freeze({}),
    feeds: Object.freeze({}),
    user: Object.freeze({
      streams: [],
      feeds: []
    }),
    curatedStreams: Object.freeze({
      popular: Object.freeze([]),
      industries: Object.freeze([])
    }),
    recentSources: Object.freeze({}),
    selectedFeedsToSave: [],
    cache: Object.freeze({
      data: Object.freeze({}),
      sources: Object.freeze({})
    }),
    favorites: Object.freeze({
      saved: Object.freeze({
        [PHOTO_TYPE]: {},
        [ARTICLE_TYPE]: {},
        [VIDEO_TYPE]: {},
        [GIF_TYPE]: {},
        [STOCK_PHOTO_TYPE]: {},
        [STOCK_VIDEO_TYPE]: {},
        [STOCK_GIF_TYPE]: {},
        [STATUS_TYPE]: {},
        [TEXT_TYPE]: {}
      }),
      added: Object.freeze({
        [PHOTO_TYPE]: {},
        [ARTICLE_TYPE]: {},
        [VIDEO_TYPE]: {},
        [GIF_TYPE]: {},
        [STOCK_PHOTO_TYPE]: {},
        [STOCK_VIDEO_TYPE]: {},
        [STOCK_GIF_TYPE]: {},
        [STATUS_TYPE]: {},
        [TEXT_TYPE]: {}
      }),
      removed: Object.freeze({
        [PHOTO_TYPE]: {},
        [ARTICLE_TYPE]: {},
        [VIDEO_TYPE]: {},
        [GIF_TYPE]: {},
        [STOCK_PHOTO_TYPE]: {},
        [STOCK_VIDEO_TYPE]: {},
        [STOCK_GIF_TYPE]: {},
        [STATUS_TYPE]: {},
        [TEXT_TYPE]: {}
      }),
      live: {},
      isFetching: false
    }),
    streamGroups: [],
    blocks: [],
    panels: [],
    groups: {},
    recentStatusesSearches: [],
    recentQuotesSearches: [],
    selection: {
      items: {},
      itemsOnPage: {},
      panelOpen: false,
      liveArticleData: {}
    }
  })
}

export default ContentState
